/**
 * Canonical GST state codes.
 * @file src/api/lib/gst-states.ts
 *
 * The first two characters of every GSTIN are the state code, and GSTR-1's
 * `pos` field carries that code — never a state name. Tally, however, exports
 * `placeOfSupply` as a NAME on most ledgers and occasionally as a code, so the
 * two shapes coexist in imported data (verified in production: 703 rows held a
 * name, 1 held "24").
 *
 * Since place-of-supply decides interstate (IGST) vs intrastate (CGST+SGST),
 * a mixed representation silently misclassifies tax. Everything that compares
 * or files a place of supply should normalise through `toStateCode()` first.
 *
 * @llm-rule WHEN: Comparing, storing or filing any place of supply / state
 * @llm-rule AVOID: Comparing raw placeOfSupply strings — they are not one shape
 * @llm-rule NOTE: 25 and 28 are legacy codes kept for historical vouchers only
 */

/** code → canonical name, per the GST portal's state master. */
export const GST_STATES: Record<string, string> = {
  '01': 'Jammu and Kashmir',
  '02': 'Himachal Pradesh',
  '03': 'Punjab',
  '04': 'Chandigarh',
  '05': 'Uttarakhand',
  '06': 'Haryana',
  '07': 'Delhi',
  '08': 'Rajasthan',
  '09': 'Uttar Pradesh',
  '10': 'Bihar',
  '11': 'Sikkim',
  '12': 'Arunachal Pradesh',
  '13': 'Nagaland',
  '14': 'Manipur',
  '15': 'Mizoram',
  '16': 'Tripura',
  '17': 'Meghalaya',
  '18': 'Assam',
  '19': 'West Bengal',
  '20': 'Jharkhand',
  '21': 'Odisha',
  '22': 'Chhattisgarh',
  '23': 'Madhya Pradesh',
  '24': 'Gujarat',
  // 25 (Daman and Diu) and 26 (Dadra and Nagar Haveli) merged in 2020 into 26.
  // 25 is retained so pre-merger vouchers still resolve.
  '25': 'Daman and Diu',
  '26': 'Dadra and Nagar Haveli and Daman and Diu',
  '27': 'Maharashtra',
  // 28 was undivided Andhra Pradesh; post-bifurcation AP is 37. Retained for
  // historical documents only — never assign it to a new registration.
  '28': 'Andhra Pradesh (pre-2014)',
  '29': 'Karnataka',
  '30': 'Goa',
  '31': 'Lakshadweep',
  '32': 'Kerala',
  '33': 'Tamil Nadu',
  '34': 'Puducherry',
  '35': 'Andaman and Nicobar Islands',
  '36': 'Telangana',
  '37': 'Andhra Pradesh',
  '38': 'Ladakh',
  '97': 'Other Territory',
  '99': 'Centre Jurisdiction',
};

/** name (lowercased, punctuation-stripped) → code. Built once at module load. */
const BY_NAME: Record<string, string> = (() => {
  const norm = (s: string) => s.toLowerCase().replace(/[^a-z]/g, '');
  const out: Record<string, string> = {};
  for (const [code, name] of Object.entries(GST_STATES)) {
    out[norm(name)] = code;
  }
  // Aliases Tally and users actually produce.
  Object.assign(out, {
    orissa: '21',
    pondicherry: '34',
    uttaranchal: '05',
    newdelhi: '07',
    delhinct: '07',
    andaman: '35',
    dadranagarhaveli: '26',
    damananddiu: '26',
    telengana: '36', // common misspelling
    chattisgarh: '22', // common misspelling
  });
  return out;
})();

/**
 * Normalise any place-of-supply representation to a 2-digit state code.
 * Accepts a code ("24", "7", "07"), a state name, or a full GSTIN.
 * Returns null when it cannot be resolved — callers should treat that as a
 * validation issue rather than guessing.
 */
export function toStateCode(input: string | null | undefined): string | null {
  if (!input) return null;
  const raw = String(input).trim();
  if (!raw) return null;

  // A full GSTIN — take its leading state code.
  if (/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]/i.test(raw)) {
    const code = raw.slice(0, 2);
    return GST_STATES[code] ? code : null;
  }

  // A bare numeric code, with or without the leading zero.
  if (/^[0-9]{1,2}$/.test(raw)) {
    const code = raw.padStart(2, '0');
    return GST_STATES[code] ? code : null;
  }

  const code = BY_NAME[raw.toLowerCase().replace(/[^a-z]/g, '')];
  return code ?? null;
}

/** Canonical state name for a code (or for anything `toStateCode` accepts). */
export function toStateName(input: string | null | undefined): string | null {
  const code = toStateCode(input);
  return code ? (GST_STATES[code] ?? null) : null;
}

/**
 * True when supplier and recipient are in different states — i.e. IGST applies.
 * Returns null if either side cannot be resolved, so the caller can raise a
 * validation issue instead of defaulting to intrastate and under-charging tax.
 */
export function isInterState(
  supplier: string | null | undefined,
  recipient: string | null | undefined,
): boolean | null {
  const a = toStateCode(supplier);
  const b = toStateCode(recipient);
  if (!a || !b) return null;
  return a !== b;
}

/**
 * PAN embedded in a GSTIN: characters 3–12 (1-indexed), i.e. slice(2, 12).
 * Returns null unless the GSTIN is structurally valid, so a malformed value
 * never yields a plausible-looking PAN.
 */
export function panFromGstin(gstin: string | null | undefined): string | null {
  if (!gstin) return null;
  const g = String(gstin).trim().toUpperCase();
  if (!/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9A-Z]Z[0-9A-Z]$/.test(g)) return null;
  return g.slice(2, 12);
}
